import { environment } from './../environments/environment';
import { APP_INITIALIZER, ApplicationConfig, LOCALE_ID, importProvidersFrom, PLATFORM_ID } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ROUTES } from './app.routes';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { UserEffects } from '@/app/_infrastructure/adapters/store/_effects/userEffects';
import { AppStateEffects } from '@/app/_infrastructure/adapters/store/_effects/LocalStorageEffect';
import { SystemEffects } from './_infrastructure/adapters/store/system/system.effects';
import { provideEffects } from '@ngrx/effects';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';
import { NonCacheInterceptor } from '../http/cache-assets.http-interceptor';
import { CacheInterceptor } from '../http/cache.http-interceptor';
import { ParseJsonInterceptor } from '../http/parseJson.http-interceptor';
import { JsonHttpInterceptor } from '../http/json.http-interceptor';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { MetaReducer, provideStore } from '@ngrx/store';
import { JwtHttpInterceptor } from '../http/jwt.http-interceptor';
import { TokenService } from './token.service';
import { UserPrefsService } from './_core/domain/services/user-prefer/user-prefs.service';
import { LanguageService } from '../services/language.service';
import { SignatureUtils } from '../services/signature.utils';
import { AppConfiguration } from './conf/app.configuration';
import { HttpClient, HTTP_INTERCEPTORS, withInterceptorsFromDi, provideHttpClient, withFetch } from '@angular/common/http';
import { AuthService } from './auth.service';
import { Configuration } from './conf/app.constants';
import { I18nHttpInterceptor } from '../http/i18n.http-interceptor';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { reducers } from './_infrastructure/adapters/store/store.reducer';
import { localStorageSyncReducer } from './_infrastructure/adapters/store/store-local-storage.reducer';
import { ApiContextService } from './_core/domain/http/http-utils/api-context.service';
import { HttpLoaderFactory } from './_infrastructure/utils/lang/lang.utils';
import { PRIME_PATCH_PROVIDERS } from './app.patch-providers';

const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];

export function jwtHttpInterceptorFactory(authService: AuthService, http: HttpClient, configuration: Configuration, tokenService: TokenService) {
  return new JwtHttpInterceptor(authService, http, configuration, tokenService);
}

export function i18nHttpInterceptorFactory(languageService: LanguageService) {
  return new I18nHttpInterceptor(languageService);
}

export function signatureUtilsFactory(appConfiguration: AppConfiguration) {
  return new SignatureUtils(appConfiguration);
}

export function startupAppConfig(
  appConfiguration: AppConfiguration,
  appConfiguration2: ApiContextService,
  langService: LanguageService,
  configuration: Configuration,
  userPrefsService: UserPrefsService
): Function {
  return () => {
    console.log('startupAppConfig init');
    return appConfiguration.load().then(() => {
      userPrefsService.init();
      return lastValueFrom(langService.init()).then(() => {
        configuration.init(); //NO IMPLEMENT... DEPRECATED!
        appConfiguration2.init2(); //NEW APIS -> HERE!!
      });
    });
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'es' },
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: NonCacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JsonHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ParseJsonInterceptor, multi: true },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: DialogService, useClass: DialogService },
    { provide: ConfirmationService, useClass: ConfirmationService },
    { provide: DynamicDialogRef, useClass: DynamicDialogRef },
    { provide: DynamicDialogConfig, useClass: DynamicDialogConfig },
    {
      provide: JwtHttpInterceptor,
      useFactory: jwtHttpInterceptorFactory,
      deps: [AuthService, HttpClient, Configuration, TokenService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: startupAppConfig,
      deps: [AppConfiguration, ApiContextService, LanguageService, Configuration, UserPrefsService],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: i18nHttpInterceptorFactory,
      deps: [LanguageService],
      multi: true
    },
    {
      provide: SignatureUtils,
      useFactory: signatureUtilsFactory,
      deps: [AppConfiguration]
    },
    ...PRIME_PATCH_PROVIDERS,
    MessageService,
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient, PLATFORM_ID]
        }
      })
    ),
    provideAnimations(),
    provideRouter(ROUTES, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideEffects([AppStateEffects, UserEffects, SystemEffects]),
    provideStore(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false
      }
    }),
    provideStoreDevtools(
      !environment.production
        ? {
            maxAge: 25,
            autoPause: true,
            trace: true
          }
        : {}
    )
  ]
};
